import type { InputHTMLAttributes, ReactElement, ReactNode } from 'react'
import { cloneElement, forwardRef, useCallback, useState } from 'react'

import type { EmojiClickData } from 'emoji-picker-react'

import EyeIcon from '@/assets/icons/icon-eye.svg'
import { EmojiPickerPopover } from '@/components/ui/emoji-picker-popover'
import { classNames } from '@/utils/helpers.ts'

export interface TextFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
  /** @deprecated Use prefix instead */
  Icon?: ReactElement
  prefix?: ReactNode
  isPassword?: boolean
  showPrefixSeparator?: boolean
  showSuffixSeparator?: boolean
  suffix?: ReactNode
  inputClassName?: string
  iconClassName?: string
  labelClassName?: string
  /** @deprecated Use suffix instead. */
  EndIcon?: ReactElement
  withEmojiPicker?: boolean
  testId?: string
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      className,
      labelClassName,
      inputClassName,
      prefix,
      showSuffixSeparator,
      showPrefixSeparator,
      suffix,
      type,
      Icon,
      EndIcon,
      isPassword,
      withEmojiPicker,
      disabled,
      testId,
      ...props
    },
    ref,
  ) => {
    const isWithIcon = !!Icon
    const isWithEndIcon = !!EndIcon

    const [passwordVisible, setPasswordVisible] = useState(false)
    const changePasswordVisibility = useCallback(() => {
      setPasswordVisible(!passwordVisible)
    }, [passwordVisible])

    const wrapIconWithClassnames = (
      pos: 'left' | 'right',
      icon: ReactElement,
    ) => {
      return cloneElement(icon, {
        className: classNames(
          'pointer-events-none w-4 h-4 absolute top-1/2 transform -translate-y-1/2',
          pos === 'left' ? ' left-3' : 'right-11 translate-x-[-100%]',
          icon?.props.className,
        ),
      })
    }

    const handleEmojiClick = useCallback(
      (emoji: EmojiClickData) => {
        props.onChange?.({
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          target: { value: (props.value ?? '') + emoji.emoji },
        })
      },
      [props],
    )

    return (
      <label
        className={classNames(
          'bg-card focus-within:border-primary-500 focus:border-primary-500 group relative flex w-full items-center gap-2 rounded-md border px-3 py-2 font-medium',
          labelClassName,
          className,
          disabled && 'pointer-events-none cursor-not-allowed',
        )}
      >
        {isWithIcon && wrapIconWithClassnames('left', Icon)}
        {prefix}
        {prefix && showPrefixSeparator && (
          <span className="bg-secondary-100 dark:bg-secondary-500 h-4 w-[1px]">
            &nbsp;
          </span>
        )}
        <input
          data-testid={testId}
          ref={ref}
          type={passwordVisible ? 'text' : type}
          className={classNames(
            'text-title placeholder:text-secondary-300 peer block w-full flex-1 bg-transparent placeholder:text-sm focus:outline-none focus:ring-0',
            disabled && 'pointer-events-none cursor-not-allowed opacity-50',
            inputClassName,
          )}
          {...props}
        />
        {suffix && showSuffixSeparator && (
          <span className="bg-secondary-100 dark:bg-secondary-500 h-4 w-[1px]">
            &nbsp;
          </span>
        )}
        {suffix}
        {isPassword && (
          <EyeIcon
            onClick={changePasswordVisibility}
            cursor="pointer"
          />
        )}
        {isWithEndIcon && wrapIconWithClassnames('right', EndIcon)}
        {withEmojiPicker && (
          <EmojiPickerPopover
            onEmojiClick={handleEmojiClick}
            small
          />
        )}
      </label>
    )
  },
)

TextField.displayName = 'TextField'
